import {useCallback} from 'react'
import {useCAPIQuery} from './useCAPIQuery'
import {useQueryClient} from '@github-ui/react-query'
import {useAgentSessionsAlive} from './use-agent-sessions-alive'
import {useDebounce} from '@github-ui/use-debounce'
import {fetchAllEvents, type EventsQueryData} from '../utils/fetch-session-events'
import {buildConversationModel} from '../utils/build-conversation-model'
import type {ConversationModel, SessionEvent} from '../types/events'

export interface SessionEventsData {
  events: SessionEvent[]
  total: number
  model: ConversationModel
}

export interface UseSessionEventsOptions {
  /** The task ID to fetch events for */
  taskId: string | undefined
  /** Whether to enable the query */
  enabled?: boolean
  /** Polling interval in milliseconds (used as fallback when Alive is not connected) */
  pollingInterval?: number
}

export function generateEventsQueryKey(taskId: string | undefined): string[] {
  return ['agent-session-events', taskId ?? '']
}

/**
 * Hook that fetches and accumulates session events for a task.
 * Uses Alive websocket push notifications to trigger refetches instantly.
 * Falls back to polling when Alive is not connected.
 * Uses cursor-based incremental loading via the `after` cursor.
 *
 * Returns events, total count, and a ConversationModel built via select.
 */
export function useSessionEvents({taskId, enabled = true, pollingInterval = 3000}: UseSessionEventsOptions) {
  const queryClient = useQueryClient()
  const queryKey = generateEventsQueryKey(taskId)

  const handleEventUpdate = useCallback(() => {
    queryClient.invalidateQueries({queryKey: generateEventsQueryKey(taskId)})
  }, [queryClient, taskId])

  const throttledHandleEventUpdate = useDebounce(handleEventUpdate, pollingInterval, {
    leading: true,
    trailing: true,
    maxWait: pollingInterval,
  })

  const aliveEnabled = enabled && !!taskId

  const {isConnected: aliveIsConnected} = useAgentSessionsAlive({
    onEvent: throttledHandleEventUpdate,
    enableCreateEvents: false,
    enableUpdateEvents: false,
    enableLogUpdates: true,
    enabled: aliveEnabled,
  })

  const query = useCAPIQuery<EventsQueryData, SessionEventsData>({
    queryKey,
    queryFn: async () => {
      if (!taskId) throw new Error('No task ID provided')
      const qk = generateEventsQueryKey(taskId)
      const prev = queryClient.getQueryData<EventsQueryData>(qk)
      return fetchAllEvents(taskId, prev)
    },
    select: (d: EventsQueryData) => ({
      events: d.events,
      total: d.total,
      model: buildConversationModel(d.events),
    }),
    // Use polling as fallback when Alive is not connected
    refetchInterval: aliveIsConnected ? false : pollingInterval,
    enabled: enabled && !!taskId,
  })

  return {
    ...query,
    aliveIsConnected,
  }
}
